import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const normalizePath = (path) => {
  if (path == null || path === '') return '/';
  const trimmed = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  return trimmed || '/';
};

/** Match sidebar item to current route (item.id is not always a URL segment, e.g. dashboard id "default" vs /dashboard). */
const routeMatchesPathname = (pathname, menuItem) => {
  if (!menuItem?.url) return false;
  const path = normalizePath(pathname);
  const url = normalizePath(menuItem.url);
  if (path === url) return true;
  // Dashboard is served at both / and /dashboard; menu item uses /dashboard
  if (url === '/dashboard' && path === '/') return true;
  // Nested routes, e.g. /support/ticket under menu url /support
  if (url !== '/' && path.startsWith(`${url}/`)) return true;
  // Legacy: URL segment equals item.id (some menus use id aligned with path)
  const segments = pathname.split('/').filter(Boolean);
  return segments.includes(menuItem.id);
};

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const customization = useSelector((state) => state.customization);
  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
  const isActive = customization.isOpen.findIndex((id) => id === item?.id) > -1;
  const matchesRoute = routeMatchesPathname(pathname, item);
  const highlighted = isActive || matchesRoute;

  const Icon = item.icon;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.5} size="1.3rem" color={highlighted ? 'white' : 'black'} />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: highlighted ? 8 : 6,
        height: highlighted ? 8 : 6,
        color: highlighted ? 'white' : 'black'
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    localStorage.setItem('selectedNavItemId', id);
    dispatch({ type: MENU_OPEN, id });
    if (matchesSM) dispatch({ type: SET_MENU, opened: false });
  };

  // Keep Redux + localStorage in sync with the URL (login redirect, logo link, deep links).
  // Do not dispatch savedId from every row — that overwrote the correct item after navigation.
  useEffect(() => {
    if (matchesRoute) {
      dispatch({ type: MENU_OPEN, id: item.id });
      localStorage.setItem('selectedNavItemId', item.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, item.id, item.url]);

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      sx={{
        borderRadius: `${customization.borderRadius}px`,
        mb: 0.5,
        alignItems: 'flex-start',
        backgroundColor: 'white',
        py: level > 1 ? 1 : 1.25,
        pl: `${level * 24}px`,
        '&:hover': {
          backgroundColor: '#430074', // Change the background color on hover
          '& .MuiTypography-root': {
            color: 'white' // Change text color on hover
          }
        }
      }}
      selected={highlighted}
      onClick={() => itemHandler(item.id)}
    >
      <ListItemIcon
        sx={{
          my: 'auto',
          minWidth: !item?.icon ? 18 : 36
        }}
        color={highlighted ? 'white' : 'black'}
      >
        {itemIcon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography
            sx={{
              color: highlighted ? 'white' : 'black',
              '&:hover': {
                color: 'white' // Ensures text stays white on hover
              }
            }}
            color="inherit"
          >
            {item.title}
          </Typography>
        }
        secondary={
          item.caption && (
            <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
              {item.caption}
            </Typography>
          )
        }
      />
      {item.chip && (
        <Chip
          color="white"
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
